/**
 * NovaAskChip — NOVA-NUDGE-1. The teach-the-workflow chip that lives
 * beneath empty states across the app: a tiny Nova, the exact
 * sentence you'd say to her, and an "Ask Nova" affordance.
 *
 * Tapping it routes to `/assistant?ask=<sentence>` — the Assistant
 * pre-fills the composer with the sentence (never auto-sends; the
 * studio hits send themselves). Nova then does the thing via her
 * NOVA-ACT-1 tools and her action card teaches the surface passively.
 *
 * Styling: glass pill per the app's dark editorial language
 * (rounded-full, white/[0.06] fill, white/[0.10] hairline) with
 * token-driven light-mode fallbacks so it stays legible on the
 * cream shell — same `base + dark:` pattern the New* empty cards use.
 */
import { useNavigate } from 'react-router-dom';
import { ArrowUpRight } from 'lucide-react';
import { OrbitIcon } from "@/components/icons/OrbitIcon";
import { cn } from '@/lib/utils';
import { NovaIcon } from '@/components/nova/NovaIcon';

export interface NovaAskChipProps {
  /** The exact sentence to pre-fill in Nova's composer. */
  ask: string;
  className?: string;
}

export function NovaAskChip({ ask, className }: NovaAskChipProps) {
  const navigate = useNavigate();
  return (
    <button
      type="button"
      onClick={() => navigate(`/assistant?ask=${encodeURIComponent(ask)}`)}
      className={cn(
        'group inline-flex max-w-full items-center gap-2 rounded-full border px-3.5 py-1.5 text-left transition-colors',
        'border-border bg-card/70 hover:bg-card',
        'dark:border-white/[0.10] dark:bg-white/[0.06] dark:hover:border-white/[0.18] dark:hover:bg-white/[0.08]',
        // BRAND-2 — focus ring follows the section accent so the
        // chip belongs to the feature it nudges from.
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-section',
        className,
      )}
    >
      <NovaIcon className="w-4 h-4" />
      <span className="min-w-0 truncate text-[13px] text-muted-foreground dark:text-white/70">
        &ldquo;{ask}&rdquo;
      </span>
      {/* BRAND-2 — "Ask Nova" wears the SECTION accent, not a raw
          blue (the old `hsl(205 65% 55%)` literal predated the token
          system).  Rennan's call: the nudge chips pick up their
          page's colour; Nova's own /assistant surface resolves this
          to Blueprint Cyan anyway.  `text-section` is theme-aware —
          AA on card in both modes per docs/orbit-palette.md. */}
      <span className="inline-flex shrink-0 items-center gap-0.5 text-[12px] font-semibold text-section">
        Ask Nova
        <OrbitIcon name="arrow.up.right" fallback={ArrowUpRight} className="w-3.5 h-3.5 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
      </span>
    </button>
  );
}

export default NovaAskChip;
